<template>
  <div class="p-7">
    <h1>Table Management</h1>
    <div>
      <input v-model="numTables" type="number" placeholder="Number of tables" />
      <button @click="generateTables">Generate Tables</button>
    </div>
    <div v-if="tables.length > 0">
      <h2>Assign Users to Tables</h2>
      <div v-for="table in sortedTables" :key="table.id" class="grid">
        <Card
          class="col mb-5"
          style="margin-bottom: 1rem; background-color: #ffffff"
        >
          <template #title>
            <div class="grid">
              <div class="col">
                <h3>{{ table.name }}</h3>
              </div>
              <div class="col-1">
                <PrimeButton
                  icon="pi pi-trash"
                  class="p-button-rounded p-button-danger p-button-text"
                  @click="confirmDeleteTable(table.id)"
                />
              </div>
            </div>
          </template>
          <template #content>
            <ul v-if="usersByTable[table.id] && usersByTable[table.id].length">
              <li
                v-for="user in usersByTable[table.id]"
                :key="user.id"
              >
                {{ user.firstName }} {{ user.lastName }}
                <PrimeButton
                  icon="pi pi-times"
                  class="p-button-rounded p-button-danger p-button-text"
                  @click="removeUserFromTable(table.id, user)"
                />
              </li>
            </ul>
            <p v-else class="empty-state">Inga användare kopplade.</p>
          </template>
          <template #footer>
            <Divider />
            <h6>Assign user to table</h6>
            <PDropdown
              v-model="selectedUsers[table.id]"
              :options="availableUsers"
              optionLabel="name"
              placeholder="Select a User"
              :style="{ width: '100%' }"
              class="m-2"
            >
              <template #option="slotProps">
                <div>
                  {{ slotProps.option.firstName }}
                  {{ slotProps.option.lastName }}
                </div>
              </template>
              <template #value="slotProps">
                <div v-if="slotProps.value">
                  {{ slotProps.value.firstName }} {{ slotProps.value.lastName }}
                </div>
                <span v-else>{{ slotProps.placeholder }}</span>
              </template>
            </PDropdown>
            <p>
              <PrimeButton
                rounded
                label="Assign User"
                @click="assignUserToTable(table.id, selectedUsers[table.id])"
              />
            </p>
          </template>
        </Card>
      </div>
    </div>
    <PrimeDialog header="Confirm" v-model:visible="dialogVisible" modal>
      <p>Are you sure you want to delete this table?</p>
      <div class="p-d-flex p-jc-end">
        <PrimeButton
          label="No"
          icon="pi pi-times"
          @click="dialogVisible = false"
          class="p-button-text"
        />
        <PrimeButton
          label="Yes"
          icon="pi pi-check"
          @click="deleteTable(confirmTableId)"
          autoFocus
        />
      </div>
    </PrimeDialog>
  </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  getDoc,
  deleteDoc,
  doc,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";
import PDropdown from "primevue/dropdown";

export default {
  components: {
    PDropdown,
  },
  setup() {
    const db = getFirestore();
    const numTables = ref(0);
    const tables = ref([]);
    const users = ref([]);
    const selectedUsers = ref({});
    const dialogVisible = ref(false);
    const confirmTableId = ref(null);

    const fetchUsers = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, "users"));
        users.value = usersSnapshot.docs.map((doc) => {
          const userData = doc.data();
          return {
            id: doc.id,
            ...userData,
            name: `${userData.firstName} ${userData.lastName}`, // Combine firstName and lastName
          };
        });
      } catch (error) {
        console.error("Error fetching users: ", error);
      }
    };

    const fetchTables = async () => {
      const tablesSnapshot = await getDocs(collection(db, "tables"));
      tables.value = tablesSnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          users: Array.isArray(data.users) ? data.users : [],
          selectedUser: null,
        };
      });
    };

    /**
     * Derive the authoritative bord→användare-mappning från user-docen.
     *
     * Historically the app stored this in two places (tables.users[] och
     * users.assignedTable). Importflödet uppdaterar bara user-docen, så den
     * är sanningen. Vi grupperar användarna per bord-id och matchar även
     * på bordsnamn som fallback för äldre poster som saknar assignedTable.
     */
    const usersByTable = computed(() => {
      const map = {};
      for (const table of tables.value) map[table.id] = [];

      const nameToId = {};
      for (const table of tables.value) nameToId[table.name] = table.id;

      for (const user of users.value) {
        let tableId = user.assignedTable;
        if (!tableId && user.assignedTableName) {
          tableId = nameToId[user.assignedTableName];
        }
        if (tableId && map[tableId]) {
          map[tableId].push(user);
        }
      }

      // Sortera namn alfabetiskt per bord för stabil visning.
      for (const id of Object.keys(map)) {
        map[id].sort((a, b) => {
          const an = `${a.firstName || ""} ${a.lastName || ""}`.toLowerCase();
          const bn = `${b.firstName || ""} ${b.lastName || ""}`.toLowerCase();
          return an.localeCompare(bn, "sv");
        });
      }

      return map;
    });

    // Alla användare som redan ligger på något bord (enligt user-docen).
    const assignedUserIds = computed(() => {
      const ids = new Set();
      for (const list of Object.values(usersByTable.value)) {
        for (const u of list) ids.add(u.id);
      }
      return ids;
    });

    const generateTables = async () => {
      const maxTableNumber = tables.value.reduce((max, table) => {
        const tableNumber = parseInt(table.name.replace("Table ", ""), 10);
        return Math.max(max, tableNumber);
      }, 0);

      for (let i = 1; i <= numTables.value; i++) {
        const tableName = `Table ${maxTableNumber + i}`;
        await addDoc(collection(db, "tables"), { name: tableName, users: [] });
      }
      fetchTables();
    };

    const confirmDeleteTable = (tableId) => {
      confirmTableId.value = tableId;
      dialogVisible.value = true;
    };

    const deleteTable = async (tableId) => {
      const tableRef = doc(db, "tables", tableId);
      await deleteDoc(tableRef);
      dialogVisible.value = false;
      fetchTables();
    };

    const assignUserToTable = async (tableId, selectedUser) => {
      if (!selectedUser) return;

      const tableRef = doc(db, "tables", tableId);
      const userRef = doc(db, "users", selectedUser.id);

      const tableSnapshot = await getDoc(tableRef);
      if (!tableSnapshot.exists()) {
        alert("Table does not exist.");
        return;
      }
      const tableData = tableSnapshot.data();

      if (assignedUserIds.value.has(selectedUser.id)) {
        alert("User är redan kopplad till ett bord.");
        return;
      }

      // Primär sanning: user-docen.
      await updateDoc(userRef, {
        assignedTable: tableId,
        assignedTableName: tableData.name,
      });

      // Håll även tables.users[] i synk för bakåtkompatibilitet med andra
      // delar av appen som kan läsa därifrån.
      await updateDoc(tableRef, {
        users: arrayUnion({
          id: selectedUser.id,
          firstName: selectedUser.firstName,
          lastName: selectedUser.lastName,
        }),
      });

      selectedUsers.value[tableId] = null;
      await Promise.all([fetchUsers(), fetchTables()]);
    };

    const removeUserFromTable = async (tableId, tableUser) => {
      const tableRef = doc(db, "tables", tableId);

      // Rensa user-docen (sanningen).
      try {
        const userRef = doc(db, "users", tableUser.id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          await updateDoc(userRef, {
            assignedTable: null,
            assignedTableName: null,
          });
        }
      } catch (e) {
        console.warn("Could not update user doc (may have been deleted):", e);
      }

      // Städa även bort ev. stale-poster från tables.users[] — vi kan inte
      // lita på exakt objekt-likhet (namn kan ha uppdaterats sedan), så vi
      // filtrerar på id och skriver tillbaka hela arrayen.
      try {
        const tableSnap = await getDoc(tableRef);
        if (tableSnap.exists()) {
          const current = Array.isArray(tableSnap.data().users)
            ? tableSnap.data().users
            : [];
          const filtered = current.filter((u) => u.id !== tableUser.id);
          if (filtered.length !== current.length) {
            await updateDoc(tableRef, { users: filtered });
          }
        }
      } catch (e) {
        console.warn("Could not clean tables.users[]:", e);
      }

      await Promise.all([fetchUsers(), fetchTables()]);
    };

    const availableUsers = computed(() => {
      return users.value.filter(
        (user) => !assignedUserIds.value.has(user.id)
      );
    });

    const sortedTables = computed(() => {
      return [...tables.value].sort((a, b) => {
        const tableNumberA = parseInt(a.name.replace("Table ", ""), 10);
        const tableNumberB = parseInt(b.name.replace("Table ", ""), 10);
        return tableNumberA - tableNumberB;
      });
    });


    onMounted(() => {
      fetchUsers();
      fetchTables();
    });

    return {
      numTables,
      tables,
      users,
      availableUsers,
      sortedTables,
      usersByTable,
      selectedUsers,
      generateTables,
      assignUserToTable,
      removeUserFromTable,
      confirmDeleteTable,
      deleteTable,
      dialogVisible,
      confirmTableId,
    };
  },
};
</script>

<style scoped>
ul {
  list-style-type: none;
  padding: 0;
}
ul li {
  display: flex;
  align-items: center;
}
.p-button-label {
  font-size: 14px;
}
.empty-state {
  color: #888;
  font-style: italic;
  margin: 0.25rem 0 0.5rem;
}
</style>
